import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { OnboardingData } from "@/types/onboarding";
import { ArrowLeft, ArrowRight } from "lucide-react";

interface ContactInfoStepProps {
  data: OnboardingData;
  onChange: (updates: Partial<OnboardingData>) => void;
  onNext: () => void;
  onBack: () => void;
}

const countries = [
  "United States",
  "United Kingdom",
  "Canada",
  "Australia",
  "Germany",
  "France",
  "Spain",
  "Italy",
  "Netherlands",
  "Belgium",
  "Switzerland",
  "Austria",
  "Sweden",
  "Norway",
  "Denmark",
  "Finland",
  "Poland",
  "Portugal",
  "Greece",
  "Ireland",
  "New Zealand",
  "South Africa",
  "Brazil",
  "Mexico",
  "Argentina",
  "Chile",
  "Japan",
  "South Korea",
  "China",
  "India",
  "Singapore",
  "Malaysia",
  "Thailand",
  "Philippines",
  "Indonesia",
  "Vietnam",
  "United Arab Emirates",
  "Saudi Arabia",
  "Egypt",
  "Nigeria",
  "Kenya",
  "Ghana",
  "Other",
];

export default function ContactInfoStep({ data, onChange, onNext, onBack }: ContactInfoStepProps) {
  return (
    <div className="space-y-6">
      {/* Contact section */}
      <fieldset className="space-y-4">
        <legend className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
          Contact
        </legend>

        <div className="space-y-1.5">
          <Label htmlFor="contactPhone">Phone number</Label>
          <Input
            id="contactPhone"
            type="tel"
            value={data.contactPhone}
            onChange={(e) => onChange({ contactPhone: e.target.value })}
            placeholder="+1 (555) 123-4567"
          />
        </div>
      </fieldset>

      {/* Section divider */}
      <div className="border-t border-slate-200" />

      {/* Address section */}
      <fieldset className="space-y-4">
        <legend className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
          Location
        </legend>

        <div className="space-y-1.5">
          <Label htmlFor="address">Street address</Label>
          <Input
            id="address"
            value={data.address}
            onChange={(e) => onChange({ address: e.target.value })}
            placeholder="123 Main Street"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="city">City</Label>
            <Input
              id="city"
              value={data.city}
              onChange={(e) => onChange({ city: e.target.value })}
              placeholder="New York"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="state">State / Province</Label>
            <Input
              id="state"
              value={data.state}
              onChange={(e) => onChange({ state: e.target.value })}
              placeholder="NY"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="country">Country</Label>
            <Select value={data.country} onValueChange={(value) => onChange({ country: value })}>
              <SelectTrigger id="country">
                <SelectValue placeholder="Select a country" />
              </SelectTrigger>
              <SelectContent>
                {countries.map((country) => (
                  <SelectItem key={country} value={country}>
                    {country}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="postalCode">Postal / ZIP code</Label>
            <Input
              id="postalCode"
              value={data.postalCode}
              onChange={(e) => onChange({ postalCode: e.target.value })}
              placeholder="10001"
            />
          </div>
        </div>
      </fieldset>

      {/* Section divider */}
      <div className="border-t border-slate-200" />

      {/* Preferences section */}
      <fieldset className="space-y-4">
        <legend className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
          Preferences
        </legend>

        <div className="space-y-1.5">
          <Label htmlFor="defaultLocale">Default language</Label>
          <Select
            value={data.defaultLocale}
            onValueChange={(value: OnboardingData["defaultLocale"]) =>
              onChange({ defaultLocale: value })
            }
          >
            <SelectTrigger id="defaultLocale" className="sm:max-w-[240px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="ar">Arabic</SelectItem>
              <SelectItem value="es">Spanish</SelectItem>
              <SelectItem value="fr">French</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </fieldset>

      {/* Navigation */}
      <div className="flex justify-between pt-2">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Button onClick={onNext} size="lg">
          Continue
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
